import type { MouseEvent } from 'react';
import { Link } from 'react-router-dom';

import { Icon } from '~/components/Icon/Icon';
import { cn } from '~/lib/cn';

interface PageButtonProps {
  onPrevious?: (event: MouseEvent) => void;
  onNext?: (event: MouseEvent) => void;
  currentPage: number;
  totalPages: number;
  nextLink?: string;
  previousLink?: string;
}

export function PageButtons({
  onPrevious,
  onNext,
  currentPage,
  totalPages,
  nextLink,
  previousLink,
}: PageButtonProps) {
  const isPreviousDisabled = currentPage === 1;
  const isNextDisabled = currentPage === totalPages;

  return (
    <div className="flex">
      {['previous', 'next'].map((position) => {
        const isPrevious = position === 'previous';
        const isDisabled = isPrevious ? isPreviousDisabled : isNextDisabled;
        const handleButtonClick = isPrevious ? onPrevious : onNext;
        const iconType = 'angle-' + (isPrevious ? 'left' : 'right');
        const ariaLabel = `pagination ${position} arrow`;
        const link = isPrevious ? previousLink : nextLink;
        const roundedClassName = isPrevious ? 'rounded-l-sm' : 'rounded-r-sm';

        const baseClasses = cn(
          'flex cursor-pointer justify-center px-px py-sm duration-300 bg-level-03 text-default',
          roundedClassName,
          'disabled:cursor-not-allowed disabled:text-disabled',
          {
            'hover:bg-primary-hover': !isDisabled,
          },
        );

        const iconComponent = (
          <Icon
            className="mx-px w-xl"
            type={iconType}
            size="md"
            aria-label={ariaLabel}
            data-testid={`pagination-${position}-arrow`}
          />
        );

        return link ? (
          <Link
            className={cn(baseClasses, 'text-center')}
            key={position}
            to={link}
            onClick={handleButtonClick}
            aria-label={`${position} page link`}
          >
            {iconComponent}
          </Link>
        ) : (
          <button
            key={position}
            className={baseClasses}
            onClick={handleButtonClick}
            disabled={isDisabled}
            aria-label={`${position} page button`}
          >
            {iconComponent}
          </button>
        );
      })}
    </div>
  );
}

PageButtons.displayName = 'PageButtons';
